import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, History as HistoryIcon, TrendingUp, Flame, MessageCircle, Heart, ArrowRight, Crown,
} from "lucide-react";
import { API, authHeaders } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";

export default function Dashboard() {
  const { t, lang } = useLang();
  const pl = lang === "pl";
  const [stats, setStats] = useState({ total: 0, premium: 0, avg_score: 0, last_created_at: null });
  const [recent, setRecent] = useState([]);
  const [ent, setEnt] = useState({ credits: 0, subscription_active: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [s, r, e] = await Promise.all([
          axios.get(`${API}/stats`, { headers: authHeaders() }),
          axios.get(`${API}/reports`, { headers: authHeaders() }),
          axios.get(`${API}/payments/entitlements`, { headers: authHeaders() }),
        ]);
        if (!cancelled) {
          setStats(s.data);
          setRecent(r.data.slice(0, 4));
          setEnt(e.data);
        }
      } catch {/* */} finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#222636] bg-[#181b24] px-3 py-1 text-xs text-[#a8adbd]">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#32d583]" />{t("dashboard")}
        </div>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">{t("appName")}</h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-[#a8adbd]">{t("tagline")}</p>
      </motion.div>

      {/* Quick stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={<TrendingUp className="h-5 w-5" />} label={t("totalReports")} value={stats.total} testId="stat-total" />
        <StatCard icon={<Crown className="h-5 w-5 text-[#fdb022]" />} label={t("premiumBadge")} value={stats.premium || 0} testId="stat-premium" />
        <StatCard icon={<Flame className="h-5 w-5" />} label={t("avgScore")} value={stats.avg_score ? `${stats.avg_score}` : "—"} testId="stat-avg" />
        <StatCard icon={<HistoryIcon className="h-5 w-5" />} label={t("lastReport")} value={stats.last_created_at ? new Date(stats.last_created_at).toLocaleDateString() : "—"} testId="stat-last" />
      </div>

      {/* Main actions */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Link to="/analyze" data-testid="tile-new-analysis"
          className="group relative col-span-1 overflow-hidden rounded-3xl border border-[#222636] bg-[#181b24] p-8 transition-all hover:-translate-y-1 hover:border-[#ff4458]/50 hover:shadow-[0_16px_60px_rgba(253,38,122,0.25)] lg:col-span-2 lg:p-10">
          <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-gradient-to-br from-[#ff4458]/30 via-[#fd267a]/20 to-transparent blur-3xl" />
          <div className="relative">
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[#32d583]/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-[#32d583]">
              {pl ? "Za darmo" : "Free"}
            </div>
            <div className="mb-6 inline-flex items-center justify-center rounded-2xl dc-gradient-bg p-3 shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white">{pl ? "Oceń swój profil" : "Rate your own profile"}</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-[#a8adbd]">
              {pl ? "Analiza dotycząca tylko Ciebie — na podstawie opisu. Bez zdjęć, bez opłat."
                  : "Analysis about you only — based on your description. No photos, no payment."}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 font-display text-sm font-semibold text-white group-hover:gap-3">
              <span className="dc-gradient-text">{t("startFirst")}</span>
              <ArrowRight className="h-4 w-4 text-[#ff4458] transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </Link>

        <Link to="/analyze/premium" data-testid="tile-premium"
          className="group relative overflow-hidden rounded-3xl border border-[#fdb022]/40 bg-gradient-to-br from-[#fdb022]/10 to-[#ff6036]/5 p-8 transition-all hover:-translate-y-1 hover:border-[#fdb022]/70">
          <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-[#fdb022]/20 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-[#fdb022]">
            <Crown className="h-3 w-3" />Premium
          </div>
          <div className="mb-6 inline-flex items-center justify-center rounded-2xl bg-[#fdb022]/15 p-3">
            <Crown className="h-6 w-6 text-[#fdb022]" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-white">{pl ? "Oceń drugą osobę" : "Rate another person"}</h2>
          <p className="mt-2 text-sm text-[#a8adbd]">
            {pl ? "Zdjęcia + opis + profil psychologiczny." : "Photos + description + psychological profile."}
          </p>
          <p className="mt-1 text-xs text-[#a8adbd]">{ent.subscription_active ? t("subscriptionActive") : `${ent.credits} ${t("creditsBalance").toLowerCase()}`}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#fdb022]">
            {pl ? "Rozpocznij" : "Start"}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      </div>

      <Link to="/history" data-testid="tile-history"
        className="group mt-5 flex items-center justify-between gap-4 rounded-3xl border border-[#222636] bg-[#181b24] p-6 transition-all hover:border-[#ff4458]/40">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#222636]">
            <HistoryIcon className="h-5 w-5 text-[#a8adbd]" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold text-white">{t("history")}</div>
            <div className="text-sm text-[#a8adbd]">{stats.total} {t("totalReports").toLowerCase()}</div>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-[#a8adbd] transition-transform group-hover:translate-x-1" />
      </Link>

      {/* Recent */}
      {recent.length > 0 && (
        <div className="mt-10">
          <h3 className="mb-4 font-display text-lg font-semibold text-white">{t("history")}</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {recent.map((r) => (
              <Link key={r.id} to={`/report/${r.id}`} data-testid={`recent-report-${r.id}`}
                className="group flex items-center justify-between gap-4 rounded-2xl border border-[#222636] bg-[#181b24] p-4 hover:border-[#ff4458]/40">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#222636] text-[#ff4458]">
                    {r.tier === "premium" ? <Crown className="h-4 w-4 text-[#fdb022]" /> : (r.mode === "photos" ? <Heart className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />)}
                  </div>
                  <div className="overflow-hidden">
                    <div className="flex items-center gap-2">
                      <div className="truncate font-display font-semibold text-white">{r.title || r.contact_name || "—"}</div>
                      {r.tier === "premium" && <span className="rounded-full bg-[#fdb022]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#fdb022]">P</span>}
                    </div>
                    <div className="truncate text-xs text-[#a8adbd]">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="font-display text-xl font-bold dc-gradient-text">{r.overall_score}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {!loading && recent.length === 0 && (
        <div className="mt-10 rounded-3xl border border-dashed border-[#222636] p-10 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#222636]">
            <Heart className="h-6 w-6 text-[#ff4458]" />
          </div>
          <h3 className="font-display text-lg font-semibold text-white">{t("noReports")}</h3>
          <p className="mt-1 text-sm text-[#a8adbd]">{t("startFirst")}</p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-[#222636] bg-[#181b24] p-5">
      <div className="mb-3 inline-flex items-center justify-center rounded-xl bg-[#222636] p-2 text-[#ff4458]">{icon}</div>
      <div className="font-display text-3xl font-bold text-white">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-[#a8adbd]">{label}</div>
    </div>
  );
}
